import { type CalendarDate, fromAbsolute, parseDate, toCalendarDate } from '@internationalized/date';
import { formatDistance } from 'date-fns';

export const timestampAttributeSorter = (attribute: string, reverse = false) => {
  // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
  return (first: any, second: any) => {
    const toDate = (ts: number | string | undefined | null) => {
      if (ts == null) return new Date();
      return new Date(typeof ts === 'number' ? ts * 1000 : ts);
    };
    const firstTimestamp = toDate(first[attribute]);
    const secondTimestamp = toDate(second[attribute]);
    if (reverse) {
      return secondTimestamp.getTime() - firstTimestamp.getTime();
    }
    return firstTimestamp.getTime() - secondTimestamp.getTime();
  };
};

export const fromNow = (epochInSeconds: number) => {
  const now = new Date();
  const timestamp = new Date(epochInSeconds * 1_000);
  return formatDistance(timestamp, now, {
    addSuffix: true,
  }).replace('about ', '');
};

export const epochNowInSeconds = () => {
  return toEpochInSeconds(new Date());
};

export const toEpochInSeconds = (date: Date) => {
  return Math.round(date.getTime() / 1_000);
};

/* ------------------------------------------------------------------------- */
/* CalendarDate <-> UTC epoch conversions (@internationalized/date)           */
/* ------------------------------------------------------------------------- */

/** Unix seconds -> `CalendarDate` (UTC), e.g. for seeding date pickers. */
export const unixTimestampToCalendarDate = (timestamp: number): CalendarDate => {
  return toCalendarDate(fromAbsolute(timestamp * 1000, 'UTC'));
};

/** `CalendarDate` -> unix seconds at 00:00:00 UTC. */
export const toUTCEpochStartOfDay = (date: CalendarDate): number => {
  return Math.floor(date.toDate('Etc/UTC').getTime() / 1000);
};

/** `CalendarDate` -> unix seconds at 23:59:59 UTC. */
export const toUTCEpochEndOfDay = (date: CalendarDate): number => {
  return toUTCEpochStartOfDay(date) + 86399;
};

/**
 * Parses a date-like wire string into a `CalendarDate`, without ever throwing.
 *
 * `parseDate` (from `@internationalized/date`) only accepts a bare `YYYY-MM-DD` string and throws a
 * `RangeError` on anything else. Some wire fields carrying this shape were previously read with
 * date-fns' `parseISO`, which tolerated a full ISO datetime string and produced an Invalid Date
 * (never a throw) for anything it couldn't make sense of. A throw is a much bigger problem here than
 * an `Invalid Date` was: several callers run inside nanostores `computed`s, where a thrown error
 * corrupts the shared notify queue for every other computed, not just the one reading this value.
 *
 * This takes just the leading `YYYY-MM-DD` portion of the string — discarding any time component,
 * which every current caller (all start/end-of-day helpers) would discard anyway — and returns
 * `undefined` instead of throwing when even that can't be parsed.
 */
export const parseDateLoosely = (date: string): CalendarDate | undefined => {
  const datePart = /^\d{4}-\d{2}-\d{2}/.exec(date)?.[0];
  if (datePart == null) {
    return undefined;
  }
  try {
    return parseDate(datePart);
  } catch {
    return undefined;
  }
};

export type FrequencyUnit = 'seconds' | 'minutes' | 'hours' | 'days' | 'weeks';

export const convertFrequencyToSeconds = (value: number, unit: FrequencyUnit) => {
  let result = value;
  switch (unit) {
    case 'weeks':
      result = result * 7;
    // fallthrough needed
    case 'days':
      result = result * 24;
    // fallthrough needed
    case 'hours':
      result = result * 60;
    // fallthrough needed
    case 'minutes':
      result = result * 60;
    // fallthrough needed
  }
  return result;
};

export const convertSecondsToFrequency = (seconds: number) => {
  let value = seconds;
  let unit: FrequencyUnit = 'seconds';
  if (seconds % 60 === 0) {
    value = seconds / 60;
    unit = 'minutes';
    if (value % 60 === 0) {
      value = value / 60;
      unit = 'hours';
      if (value % 24 === 0) {
        value = value / 24;
        unit = 'days';
        if (value % 7 === 0) {
          value = value / 7;
          unit = 'weeks';
        }
      }
    }
  }
  return { value, unit };
};

export const formatFrequency = (seconds: number) => {
  const schedule = convertSecondsToFrequency(seconds);
  const unitLabel = schedule.value === 1 ? schedule.unit.substring(0, schedule.unit.length - 1) : schedule.unit;
  return `${schedule.value} ${unitLabel}`;
};

export const MetricsDuration = {
  LastHour: '1h',
  Last24Hours: '1d',
  Last7Days: '7d',
  Last30Days: '30d',
} as const;
export type MetricsDuration = (typeof MetricsDuration)[keyof typeof MetricsDuration];

export const MetricsDurationLabels = {
  [MetricsDuration.LastHour]: 'Last hour',
  [MetricsDuration.Last24Hours]: 'Last 24 hours',
  [MetricsDuration.Last7Days]: 'Last 7 days',
  [MetricsDuration.Last30Days]: 'Last 30 days',
};

export const formatLatency = (seconds: string | null | undefined) => {
  const v = Number(seconds ?? undefined);
  let displayValue = '-';

  if (!Number.isNaN(v)) {
    if (v < 1) {
      const latencyInMs = v * 1000;
      displayValue = `${+latencyInMs.toFixed(3)}ms`;
    } else {
      displayValue = `${+v.toFixed(2)}s`;
    }
  }
  return displayValue;
};
